import React, { Component } from 'react';
import { connect } from 'react-redux';

import { TRIM_ACTION_DISPATCHED } from 'constants/messages';
import {
  ADD_TRIM_OPERATION,
  ENTER_TRIM_MODE_OPERATION,
  LEAVE_TRIM_MODE_OPERATION,
  REDO_TRIM_OPERATION,
  REMOVE_TRIM_OPERATION,
  UNDO_TRIM_OPERATION,
  UPDATE_CURRENT_TRIM_RANGE_OPERATION,
  UPDATE_SELECTED_TRIM_BOUND_OPERATION,
  UPDATE_SELECTED_TRIM_RANGE_OPERATION,
  UPDATE_TRIM_OPERATION,
} from 'constants/trimming';
import {
  addTrimRangeCreator,
  enterTrimModeCreator,
  leaveTrimModeCreator,
  redoTrimOperationCreator,
  removeTrimRangeCreator,
  undoTrimOperationCreator,
  updateCurrentTrimRangeCreator,
  updateSelectedTrimBoundCreator,
  updateSelectedTrimRangeCreator,
  updateTrimRangeCreator,
} from 'creators/trimming';
import * as sentry from 'utilities/sentry';

import CancelTrimButton from './cancel-trim-button';
import DiscardTrimButton from './discard-trim-button';
import EndCurrentTrimButton from './end-current-trim-button';
import PublishChangesButton from './publish-changes-button';
import RedoButton from './redo-button';
import TrimRangePillBar from './trim-range-pill-bar';
import UndoButton from './undo-button';

import './styles.less';

const INVALID_TRIM_OPERATION_ERROR = new Error('invalid trim operation');

class TrimBar extends Component {
  constructor(props) {
    super(props);

    this.handleDiscardSelectedTrimRange = this.handleDiscardSelectedTrimRange.bind(
      this
    );
    this.handleEndCurrentTrimRange = this.handleEndCurrentTrimRange.bind(this);
    this.handleTrimAction = this.handleTrimAction.bind(this);
  }

  render() {
    const {
      currentTrimRange,
      duration,
      newTrimChanges,
      redoStack,
      selectedTrimRange,
      undoStack,
      trimRanges,
    } = this.props;

    let cancelButton = null;
    let cutCurrentButton = null;

    let saveButton = null;
    let trimRangePillBar = null;

    if (currentTrimRange != null) {
      if (selectedTrimRange != null) {
        // if a marker was clicked while we're in edit mode, just show discard
        cancelButton = (
          <DiscardTrimButton
            selectedTrimRange={selectedTrimRange}
            onDiscardTrimRange={this.handleDiscardSelectedTrimRange}
          />
        );
      } else {
        cancelButton = (
          <CancelTrimButton
            currentTrimRange={currentTrimRange}
            onCancelCurrentTrimRange={this.props.onCancelCurrentTrimRange}
          />
        );

        cutCurrentButton = (
          <EndCurrentTrimButton
            showActionDot={this.props.showCutActionDot}
            onEndCurrentTrimRange={this.handleEndCurrentTrimRange}
          />
        );
      }

      trimRangePillBar = <TrimRangePillBar duration={duration} />;
    } else {
      cancelButton = (
        <DiscardTrimButton
          selectedTrimRange={selectedTrimRange}
          onDiscardTrimRange={this.handleDiscardSelectedTrimRange}
        />
      );

      saveButton = (
        <PublishChangesButton
          showActionDot={this.props.showPublishActionDot}
          currentTrimRange={currentTrimRange}
          newTrimChanges={newTrimChanges}
          onPublishChanges={this.props.onPublishChanges}
        />
      );
    }

    return (
      <div className="share-video-trim-bar">
        <div className="left-section">
          {trimRanges.length > 0 && (
            <React.Fragment>
              <UndoButton
                currentTrimRange={currentTrimRange}
                onUndoTrim={this.props.onUndoTrim}
                undoStack={undoStack}
              />
              <RedoButton
                currentTrimRange={currentTrimRange}
                onRedoTrim={this.props.onRedoTrim}
                redoStack={redoStack}
              />
            </React.Fragment>
          )}
        </div>
        <div className="middle-section">
          {trimRangePillBar}
          {saveButton}
        </div>
        <div className="right-section">
          {(currentTrimRange || selectedTrimRange) && cancelButton}
          {currentTrimRange && cutCurrentButton}
        </div>
      </div>
    );
  }

  componentDidMount() {
    window.top.addEventListener('message', this.handleTrimAction, false);
  }

  componentWillUnmount() {
    window.top.removeEventListener('message', this.handleTrimAction);
  }

  handleTrimAction(e) {
    const { data: eventData = {}, source } = e;

    // ignore messages coming from the share page or that aren't trim actions
    if (source === window || eventData.type !== TRIM_ACTION_DISPATCHED) {
      return;
    }

    const {
      id,
      operation,
      trimBound,
      trimRange,
      trimRanges,
      updatedVideo,
    } = eventData;

    switch (operation) {
      case ADD_TRIM_OPERATION:
        return this.props.addTrimRangeCreator(trimRange);
      case REMOVE_TRIM_OPERATION:
        return this.props.removeTrimRangeCreator(id);
      case ENTER_TRIM_MODE_OPERATION:
        return this.props.enterTrimModeCreator(trimRanges);
      case LEAVE_TRIM_MODE_OPERATION:
        return this.props.leaveTrimModeCreator(updatedVideo);
      case REDO_TRIM_OPERATION:
        return this.props.redoTrimOperationCreator();
      case UNDO_TRIM_OPERATION:
        return this.props.undoTrimOperationCreator();
      case UPDATE_CURRENT_TRIM_RANGE_OPERATION:
        return this.props.updateCurrentTrimRangeCreator(trimRange);
      case UPDATE_SELECTED_TRIM_BOUND_OPERATION:
        return this.props.updateSelectedTrimBoundCreator(trimBound);
      case UPDATE_SELECTED_TRIM_RANGE_OPERATION:
        return this.props.updateSelectedTrimRangeCreator(trimRange);
      case UPDATE_TRIM_OPERATION:
        return this.props.updateTrimRangeCreator(trimRange);
      default:
        sentry.logException(
          INVALID_TRIM_OPERATION_ERROR,
          'bad trim operation',
          {
            operation,
          }
        );
    }
  }

  handleEndCurrentTrimRange() {
    const { currentTrimRange } = this.props;

    this.props.onEndCurrentTrimRange(currentTrimRange);
  }

  handleDiscardSelectedTrimRange() {
    const { selectedTrimRange } = this.props;

    this.props.onDiscardTrimRange(selectedTrimRange.id);
  }
}

const mapDispatchToProps = dispatch => {
  return {
    addTrimRangeCreator: trimRange =>
      dispatch(addTrimRangeCreator(trimRange, false, false)),
    enterTrimModeCreator: trimRanges =>
      dispatch(enterTrimModeCreator(trimRanges, false)),
    leaveTrimModeCreator: updatedVideo =>
      dispatch(leaveTrimModeCreator(updatedVideo, false)),
    redoTrimOperationCreator: () => dispatch(redoTrimOperationCreator(false)),
    removeTrimRangeCreator: trimRangeId =>
      dispatch(removeTrimRangeCreator(trimRangeId, false, false)),
    undoTrimOperationCreator: () => dispatch(undoTrimOperationCreator(false)),
    updateCurrentTrimRangeCreator: trimRange =>
      dispatch(updateCurrentTrimRangeCreator(trimRange, false)),
    updateSelectedTrimBoundCreator: trimBound =>
      dispatch(updateSelectedTrimBoundCreator(trimBound, false)),
    updateSelectedTrimRangeCreator: trimRange =>
      dispatch(updateSelectedTrimRangeCreator(trimRange, false)),
    updateTrimRangeCreator: trimRange =>
      dispatch(updateTrimRangeCreator(trimRange, false, false)),
  };
};

export default connect(null, mapDispatchToProps)(TrimBar);
